import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import axios from "axios";
import ButtonsMainGoTo from "../../../components/Button/ButtonMainGoTo";
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { API_URL } from "../../../constants";
import { useAuth } from "../../../provider";
import * as api from "../../../Auth/auth";

// Handles the initial subscription payment's Stripe return only — addon-payment
// success is handled by LabVisits/LabvisitWorkflow/LabVisitAddonSuccess.jsx instead.
export default function WorkflowPaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const { state, authLoading, handleLogout } = useAuth();

  const [labVisitId, setLabVisitId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("Verifying your payment...");
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);

  // Use a ref to avoid double-invocation in React StrictMode
  const hasRun = useRef(false);

  useEffect(() => {
    // Wait for auth to finish loading - Auth restoration complete
    if (authLoading) return;

    if (!state.isLoggedIn) {
      setError("Authentication required. Please log in.");
      setLoading(false);
      return;
    }

    if (!sessionId) {
      setError("Missing session ID.");
      setLoading(false);
      return;
    }

    // Guard against double-fire (React StrictMode / re-renders)
    if (hasRun.current) return;
    hasRun.current = true;

    let isMounted = true;

    const verifyPayment = async () => {
      try {
        const res = await axios.post(`${API_URL}/stripe/post-success/`, { session_id: sessionId });

        if (!isMounted) return;

        // Backend returns labvisit_id from Stripe session metadata.
        const resolvedLabVisitId = res.data?.labvisit_id ?? null;
        setLabVisitId(resolvedLabVisitId);
        setMessage("Payment confirmed successfully!");
        setVerified(true);
        setLoading(false);

        // Navigate to the next workflow step, passing labvisit_id so the
        // next page can load the correct LabVisit without an extra lookup.
        setTimeout(() => {
          if (!isMounted) return;
          if (resolvedLabVisitId) {
            navigate(`/StateLabAppointment/${resolvedLabVisitId}`, {
              replace: true,
            });
          } else {
            // Fallback: no labvisit_id in metadata (shouldn't happen in prod)
            navigate("/StateLabAppointment", { replace: true });
          }
        }, 2500);
      } catch (err) {
        if (!isMounted) return;
        console.error("Payment verification failed:", err);
        setError(
          err.response?.data?.error ||
            "Payment verification failed. Please contact support."
        );
        setLoading(false);
      }
    };

    verifyPayment();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, state.isLoggedIn]);
  //  ↑ Intentionally omit sessionId / navigate from deps:
  //    they never change within a single page load and adding them risks
  //    double-firing the payment verification.

  const handleLogoutApi = async () => {
    const response = await api.logout();
    await handleLogout(response);
  };

  // --- UI ---
  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          borderRadius: 3,
          boxShadow: 3,
          backgroundColor: "background.paper",
        }}
      >
        {error ? (
          <>
            <Typography variant="h5" color="error" gutterBottom>
              ⚠️ Payment Error
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              {error}
            </Typography>
            <Button
              component={RouterLink}
              to="/"
              variant="contained"
              color="primary"
            >
              Try Again
            </Button>
          </>
        ) : verified ? (
          <>
            <Typography variant="h4" gutterBottom>
              Payment Successful! Redirecting…
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
              {message}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <ButtonsMainGoTo
                name="CONTINUE"
                to={labVisitId ? `/StateLabAppointment/${labVisitId}` : "/StateLabAppointment"}
              />
              <ButtonsMainGoTo name="LOGOUT" to="/" onClick={handleLogoutApi} />
            </Stack>
          </>
        ) : (
          <>
            <Typography variant="h5" gutterBottom>
              Processing Payment…
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {message}
            </Typography>
            <CircularProgress color="primary" />
          </>
        )}
      </Box>
    </Container>
  );
}
